/**
 * UTM Builder Utility
 *
 * Centralized utility for generating tracking URLs with UTM parameters.
 * Integrates with Usermaven analytics for attribution tracking.
 *
 * @see https://support.google.com/analytics/answer/1033863 - UTM parameter reference
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Standard UTM parameters for campaign tracking
 */
export interface UTMParams {
  /** The referrer (e.g., google, newsletter, email) */
  utm_source: string;
  /** Marketing medium (e.g., cpc, email, social, link) */
  utm_medium?: string;
  /** Campaign name (e.g., spring_sale, launch_2025) */
  utm_campaign?: string;
  /** Identifies specific content/CTA (e.g., hero_button, sidebar_link) */
  utm_content?: string;
  /** Paid search keywords */
  utm_term?: string;
}

/**
 * Ad platform click identifiers captured from URL params
 */
export interface ClickIds {
  /** Google Click ID (primary) */
  gclid?: string;
  /** Google iOS app/web interplay click ID */
  gbraid?: string;
  /** Google iOS web-only click ID */
  wbraid?: string;
  /** Facebook/Meta Click ID */
  fbclid?: string;
}

/**
 * Preset configurations for common traffic sources
 */
export type UTMPreset =
  | 'email_campaign'
  | 'email_sequence'
  | 'email_transactional'
  | 'blog_internal'
  | 'blog_cta'
  | 'social_organic'
  | 'social_paid'
  | 'cta_hero'
  | 'cta_pricing'
  | 'cta_navbar'
  | 'cta_footer'
  | 'affiliate'
  | 'direct';

/**
 * Options for URL building
 */
export interface BuildUrlOptions {
  /** Preserve existing UTM params in the URL (default: false, overwrites) */
  preserveExisting?: boolean;
  /** Skip adding UTM params entirely (opt-out flag) */
  skipTracking?: boolean;
  /** Additional query params to add (non-UTM) */
  additionalParams?: Record<string, string>;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Standard UTM parameter keys for validation
 */
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * Maximum length for UTM parameter values (prevents abuse)
 */
const UTM_MAX_VALUE_LENGTH = 200;

/**
 * Ad platform click ID parameter keys (Google Ads + Meta)
 */
export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;

/**
 * Cookie configuration for click ID persistence
 * Note: cookie name is 'jds_gclid' for historical reasons but stores all click IDs (gclid, gbraid, wbraid, fbclid)
 */
const GCLID_COOKIE_NAME = 'jds_gclid';
const GCLID_COOKIE_EXPIRY_DAYS = 90;

// ============================================================================
// Security Utilities
// ============================================================================

/**
 * Sanitize UTM parameter value to prevent XSS and limit length
 * IMPORTANT: Never include PII in UTM parameters
 *
 * @param value - Raw value from URL
 * @returns Sanitized value safe for storage and display
 */
function sanitizeUTMValue(value: string): string {
  return value
    .slice(0, UTM_MAX_VALUE_LENGTH)
    .replace(/[<>\"'&]/g, ''); // Remove XSS-prone characters
}

/**
 * Get window origin safely (SSR-compatible)
 */
function getOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/**
 * Get current search params safely (SSR-compatible)
 */
function getCurrentSearch(): string {
  if (typeof window === 'undefined') return '';
  return window.location.search;
}

/**
 * Preset configurations for common use cases
 */
export const UTM_PRESETS: Record<UTMPreset, Partial<UTMParams>> = {
  // Email presets
  email_campaign: {
    utm_source: 'email',
    utm_medium: 'email',
  },
  email_sequence: {
    utm_source: 'email',
    utm_medium: 'email_sequence',
  },
  email_transactional: {
    utm_source: 'email',
    utm_medium: 'transactional',
  },

  // Blog presets
  blog_internal: {
    utm_source: 'blog',
    utm_medium: 'link',
  },
  blog_cta: {
    utm_source: 'blog',
    utm_medium: 'cta',
  },

  // Social presets
  social_organic: {
    utm_source: 'social',
    utm_medium: 'organic',
  },
  social_paid: {
    utm_source: 'social',
    utm_medium: 'paid',
  },

  // CTA presets (internal navigation)
  cta_hero: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'hero',
  },
  cta_pricing: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'pricing',
  },
  cta_navbar: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'navbar',
  },
  cta_footer: {
    utm_source: 'website',
    utm_medium: 'cta',
    utm_content: 'footer',
  },

  // Affiliate (works alongside Push Lap Growth)
  affiliate: {
    utm_source: 'affiliate',
    utm_medium: 'referral',
  },

  // Direct/fallback
  direct: {
    utm_source: 'direct',
    utm_medium: 'none',
  },
};

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Build a URL with UTM parameters
 *
 * @param baseUrl - The target URL (can include existing query params)
 * @param params - UTM parameters to add
 * @param options - Build options
 * @returns URL string with UTM parameters
 *
 * @example
 * ```ts
 * buildTrackingUrl('https://jdsimplified.com/pricing', {
 *   utm_source: 'email',
 *   utm_medium: 'newsletter',
 *   utm_campaign: 'jan_2025'
 * });
 * // => "https://jdsimplified.com/pricing?utm_source=email&utm_medium=newsletter&utm_campaign=jan_2025"
 * ```
 */
export function buildTrackingUrl(
  baseUrl: string,
  params: Partial<UTMParams>,
  options: BuildUrlOptions = {}
): string {
  const { preserveExisting = false, skipTracking = false, additionalParams = {} } = options;

  // If tracking is disabled, return original URL with only additional params
  if (skipTracking) {
    if (Object.keys(additionalParams).length === 0) {
      return baseUrl;
    }
    const origin = getOrigin();
    if (!origin) return baseUrl; // SSR safety
    const url = new URL(baseUrl, origin);
    Object.entries(additionalParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    return url.toString();
  }

  try {
    // Parse the URL, handling relative URLs
    const origin = getOrigin();
    if (!origin) return baseUrl; // SSR safety
    const url = new URL(baseUrl, origin);

    // Handle existing UTM params
    if (!preserveExisting) {
      // Remove existing UTM params
      UTM_KEYS.forEach(key => url.searchParams.delete(key));
    }

    // Add new UTM params (only if value is provided)
    Object.entries(params).forEach(([key, value]) => {
      if (value && UTM_KEYS.includes(key as typeof UTM_KEYS[number])) {
        // If preserving existing and param exists, skip
        if (preserveExisting && url.searchParams.has(key)) {
          return;
        }
        url.searchParams.set(key, value);
      }
    });

    // Add additional non-UTM params
    Object.entries(additionalParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  } catch {
    // If URL parsing fails (e.g., mailto:, tel:), return original
    return baseUrl;
  }
}

/**
 * Build a URL using a preset configuration
 *
 * @param baseUrl - The target URL
 * @param preset - Preset configuration name
 * @param overrides - Optional overrides for the preset
 * @param options - Build options
 * @returns URL string with UTM parameters
 *
 * @example
 * ```ts
 * buildUrlFromPreset('https://jdsimplified.com/signup', 'email_campaign', {
 *   utm_campaign: 'welcome_series'
 * });
 * ```
 */
export function buildUrlFromPreset(
  baseUrl: string,
  preset: UTMPreset,
  overrides: Partial<UTMParams> = {},
  options: BuildUrlOptions = {}
): string {
  const presetParams = UTM_PRESETS[preset];
  const mergedParams = { ...presetParams, ...overrides };
  return buildTrackingUrl(baseUrl, mergedParams, options);
}

/**
 * Extract UTM parameters from a URL or the current page
 *
 * @param url - Optional URL to extract from (defaults to current page)
 * @returns Partial UTM params object (only includes present params)
 *
 * @example
 * ```ts
 * // From current page
 * const params = extractUTMParams();
 *
 * // From specific URL
 * const params = extractUTMParams('https://example.com?utm_source=email');
 * ```
 */
export function extractUTMParams(url?: string): Partial<UTMParams> {
  try {
    const searchParams = url
      ? new URL(url).searchParams
      : new URLSearchParams(getCurrentSearch());

    const params: Partial<UTMParams> = {};

    UTM_KEYS.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        // Sanitize value to prevent XSS and limit length
        params[key] = sanitizeUTMValue(value);
      }
    });

    return params;
  } catch {
    return {};
  }
}

/**
 * Check if a URL has any UTM parameters
 *
 * @param url - URL to check (defaults to current page)
 * @returns Boolean indicating presence of UTM params
 */
export function hasUTMParams(url?: string): boolean {
  const params = extractUTMParams(url);
  return Object.keys(params).length > 0;
}

/**
 * Merge UTM params with conflict resolution
 *
 * @param existing - Existing UTM params
 * @param incoming - New UTM params to merge
 * @param strategy - Conflict resolution: 'incoming' (default) or 'existing'
 * @returns Merged UTM params
 */
export function mergeUTMParams(
  existing: Partial<UTMParams>,
  incoming: Partial<UTMParams>,
  strategy: 'incoming' | 'existing' = 'incoming'
): Partial<UTMParams> {
  if (strategy === 'existing') {
    return { ...incoming, ...existing };
  }
  return { ...existing, ...incoming };
}

/**
 * Validate UTM params structure
 *
 * @param params - Params to validate
 * @returns Boolean indicating if params are valid
 */
export function validateUTMParams(params: Partial<UTMParams>): boolean {
  // utm_source is required for valid tracking
  if (!params.utm_source) {
    return false;
  }

  // All keys must be valid UTM keys
  return Object.keys(params).every(key =>
    UTM_KEYS.includes(key as typeof UTM_KEYS[number])
  );
}

// ============================================================================
// Storage & Persistence
// ============================================================================

const UTM_STORAGE_KEY = 'jds_utm_params';
const UTM_STORAGE_EXPIRY_KEY = 'jds_utm_expiry';
const UTM_DEFAULT_EXPIRY_HOURS = 24; // Persist UTM for 24 hours

/**
 * Store UTM params in sessionStorage for cross-page persistence
 *
 * @param params - UTM params to store
 * @param expiryHours - Hours until expiry (default: 24)
 */
export function storeUTMParams(
  params: Partial<UTMParams>,
  expiryHours: number = UTM_DEFAULT_EXPIRY_HOURS
): void {
  try {
    const expiry = Date.now() + (expiryHours * 60 * 60 * 1000);
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    sessionStorage.setItem(UTM_STORAGE_EXPIRY_KEY, expiry.toString());
  } catch {
    // Storage might be unavailable (private browsing, etc.)
  }
}

/**
 * Retrieve stored UTM params (if not expired)
 *
 * @returns Stored UTM params or empty object if expired/missing
 */
export function getStoredUTMParams(): Partial<UTMParams> {
  try {
    const expiryStr = sessionStorage.getItem(UTM_STORAGE_EXPIRY_KEY);
    if (!expiryStr || Date.now() > parseInt(expiryStr, 10)) {
      clearStoredUTMParams();
      return {};
    }

    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};

    try {
      const parsed = JSON.parse(stored);
      // Validate it's an object with expected structure
      if (typeof parsed !== 'object' || parsed === null) {
        clearStoredUTMParams();
        return {};
      }
      return parsed;
    } catch {
      // Clear corrupted data
      clearStoredUTMParams();
      return {};
    }
  } catch {
    return {};
  }
}

/**
 * Clear stored UTM params
 */
export function clearStoredUTMParams(): void {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
    sessionStorage.removeItem(UTM_STORAGE_EXPIRY_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Capture UTM params from current URL and store them
 * Call this on app initialization to persist attribution across navigation
 *
 * @returns The captured params (empty if none present)
 */
export function captureAndStoreUTMParams(): Partial<UTMParams> {
  const currentParams = extractUTMParams();

  if (Object.keys(currentParams).length > 0) {
    // New UTM params in URL - store them (overwrite existing)
    storeUTMParams(currentParams);
    return currentParams;
  }

  // No new params - return stored ones
  return getStoredUTMParams();
}

// ============================================================================
// Helper Functions for Common Scenarios
// ============================================================================

/**
 * Generate a tracking URL for email campaigns
 *
 * @param baseUrl - Target URL
 * @param campaignName - Name of the email campaign
 * @param emailType - Type of email (campaign, sequence, transactional)
 * @param content - Optional content identifier
 */
export function generateEmailTrackingUrl(
  baseUrl: string,
  campaignName: string,
  emailType: 'campaign' | 'sequence' | 'transactional' = 'campaign',
  content?: string
): string {
  const presetMap = {
    campaign: 'email_campaign',
    sequence: 'email_sequence',
    transactional: 'email_transactional',
  } as const;

  return buildUrlFromPreset(baseUrl, presetMap[emailType], {
    utm_campaign: campaignName,
    utm_content: content,
  });
}

/**
 * Generate a tracking URL for blog content
 *
 * @param baseUrl - Target URL
 * @param postSlug - Blog post slug for attribution
 * @param isCta - Whether this is a CTA link (vs inline link)
 */
export function generateBlogTrackingUrl(
  baseUrl: string,
  postSlug: string,
  isCta: boolean = false
): string {
  return buildUrlFromPreset(
    baseUrl,
    isCta ? 'blog_cta' : 'blog_internal',
    {
      utm_campaign: 'blog_content',
      utm_content: postSlug,
    }
  );
}

// ============================================================================
// Click ID (gclid/gbraid/wbraid) Functions
// ============================================================================

/**
 * Extract Google Ads click IDs from URL params
 *
 * @param url - Optional URL to extract from (defaults to current page)
 * @returns Click IDs found in URL
 */
export function extractClickIds(url?: string): ClickIds {
  try {
    const searchParams = url
      ? new URL(url).searchParams
      : new URLSearchParams(getCurrentSearch());

    const ids: ClickIds = {};
    CLICK_ID_KEYS.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        ids[key] = sanitizeUTMValue(value);
      }
    });
    return ids;
  } catch {
    return {};
  }
}

/**
 * Set a first-party cookie for gclid persistence (90-day expiry)
 *
 * @param clickIds - Click IDs to store
 */
export function storeClickIdCookie(clickIds: ClickIds): void {
  if (typeof document === 'undefined') return;
  try {
    const expires = new Date(Date.now() + GCLID_COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    const value = JSON.stringify(clickIds);
    document.cookie = `${GCLID_COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
  } catch {
    // Cookie storage unavailable
  }
}

/**
 * Get stored click IDs from cookie
 *
 * @returns Stored click IDs or empty object
 */
export function getStoredClickIds(): ClickIds {
  if (typeof document === 'undefined') return {};
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${GCLID_COOKIE_NAME}=([^;]*)`));
    if (!match) return {};
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed;
  } catch {
    return {};
  }
}

/**
 * Capture click IDs from URL and store in 90-day cookie.
 * New URL params overwrite existing cookie (last-touch for fresh clicks).
 * Falls back to stored cookie when no click IDs are in the URL.
 *
 * @returns The active click IDs (from URL or cookie)
 */
export function captureAndStoreClickIds(): ClickIds {
  const currentIds = extractClickIds();

  if (Object.keys(currentIds).length > 0) {
    // New click IDs in URL — store them (overwrites existing)
    storeClickIdCookie(currentIds);
    return currentIds;
  }

  // No new IDs — return stored ones
  return getStoredClickIds();
}

/**
 * Check if a URL should have tracking added
 * Excludes external domains, special protocols, and anchor links
 *
 * @param url - URL to check
 * @returns Boolean indicating if tracking should be added
 */
export function shouldAddTracking(url: string): boolean {
  // Skip special protocols (including data:, blob:, file:)
  if (/^(mailto:|tel:|javascript:|data:|blob:|file:|#)/.test(url)) {
    return false;
  }

  try {
    const origin = getOrigin();
    if (!origin) return false; // SSR safety
    const parsed = new URL(url, origin);

    // Only track links to our domains
    const ourDomains = [
      'jdsimplified.com',
      'dev.jdsimplified.com',
      'admin.jdsimplified.com',
      'localhost',
    ];

    return ourDomains.some(domain =>
      parsed.hostname === domain || parsed.hostname.endsWith('.' + domain)
    );
  } catch {
    // If URL parsing fails, skip tracking
    return false;
  }
}
